import { useCallbackRef } from '@radix-ui/react-use-callback-ref';
import * as Tone from "tone";
import type { Player } from 'tone';
import { ReactNode, useRef, useState, useCallback } from "react";
import { useStartAudioContext } from "@tmsw/powerups.audio.hooks.use-start-audio-context";
import type { AddPlayerOptions } from './context';
import { ReactAudioContext } from "./context";

export type AudioProviderProps = {
  children?: ReactNode;
}

const audioCtx = new AudioContext({
  sampleRate: 22050,
  latencyHint: 'interactive',
});
Tone.getContext().dispose();
Tone.setContext(audioCtx);

function AudioProvider({ children }: AudioProviderProps) {

  const [ready, setReady] = useState(false);

  useStartAudioContext({callback: () => {
    setReady(true);
  }});

  const existingPlayersRef = useRef<Map<string, {
    prevPlayState: 'started'|'stopped',
    pausedAt: number,
    name: string,
  }>>(new Map());

  const playersRef = useRef(new Tone.Players().toDestination() as Tone.Players);

  function getContext() {
    return Tone.getContext();
  }

  const addPlayer = useCallbackRef((options:AddPlayerOptions):Player => {
    const { name, url, callback } = options;
    if (!playersRef.current.has(name)) {
      console.log('AudioProvider addPlayer', options);
      playersRef.current.add(name, url, callback).toDestination();
      existingPlayersRef.current.set(name, {
        name,
        prevPlayState: 'stopped',
        pausedAt: 0
      });
    }
    return playersRef.current.player(name);
  });

  const pausePlayers = useCallback(() => {
    // console.log('pausePlayers', existingPlayersRef);
    existingPlayersRef.current.forEach((p) => {
      const player = playersRef.current.player(p.name);
      if(player.state === 'started') {
        existingPlayersRef.current.set(p.name, {
          name: p.name,
          pausedAt: player.now(),
          prevPlayState: 'started'
        });
      }
      player.stop();
    });
  }, []);

  const unpausePlayers = useCallback(() => {
    // console.log('unpausePlayers', existingPlayersRef);
    existingPlayersRef.current.forEach((p:any) => {
      const player = playersRef.current.player(p.name);
      if(p.prevPlayState === 'started') {
        player.start(0, p.pausedAt);
      }
    });
  }, []);

  const providerValue = {
    audioCtxStarted: ready,
    players: playersRef.current,
    addPlayer,
    getContext,
    pausePlayers,
    unpausePlayers
  };

  console.log('providerValue', providerValue);

  return (
    <ReactAudioContext.Provider value={providerValue}>
      {children}
    </ReactAudioContext.Provider>
  );
}

AudioProvider.whyDidYouRender = true;
export { AudioProvider }